import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { timeAgoAr } from "@/lib/mockData";
import { SignedImage } from "@/lib/signedFile";
import AssociationActivities from "@/pages/association/AssociationActivities";
import AgreementAlert from "@/components/AgreementAlert";
import {
  Users, GraduationCap, ClipboardCheck, AlertTriangle,
  ArrowLeft, CheckCircle2, Clock, TrendingDown,
  Sparkles, Building2, Edit3,
} from "lucide-react";

const DemoBadge = () => (
  <span
    className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 text-[10px] font-bold"
    title="بيانات توضيحية"
  >
    <Sparkles className="h-3 w-3" /> عرض توضيحي
  </span>
);

// ---------- Small reusable pieces -------------------------------------------
function StatCard({ icon: Icon, label, value, tone = "teal", to }) {
  const tones = {
    teal: "bg-teal-50 text-teal-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
    sky: "bg-sky-50 text-sky-700",
  };
  const Wrapper = to ? Link : "div";
  const wrapperProps = to ? { to } : {};
  return (
    <Wrapper
      {...wrapperProps}
      className={`p-4 rounded-2xl bg-white border border-cream-200 card-shadow ${to ? "hover:-translate-y-0.5 hover:border-teal-200 transition-all block" : ""}`}
    >
      <div className={`h-11 w-11 rounded-xl grid place-items-center mb-4 ${tones[tone]}`}>
        <Icon className="h-5 w-5" strokeWidth={1.75} />
      </div>
      <div className="text-3xl font-black text-slate-900 arabic-numerals">{value}</div>
      <div className="text-sm text-slate-500 mt-1">{label}</div>
    </Wrapper>
  );
}

function SectionHeader({ title, count, hint, action, actionTo }) {
  return (
    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
      <div>
        <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2">
          {title}
          {count != null && (
            <span className="text-xs font-bold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full arabic-numerals">
              {count}
            </span>
          )}
        </h3>
        {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
      </div>
      {action && actionTo && (
        <Link
          to={actionTo}
          className="inline-flex items-center gap-1 text-xs font-semibold text-teal-700 hover:text-teal-800"
        >
          {action} <ArrowLeft className="h-3.5 w-3.5" />
        </Link>
      )}
    </div>
  );
}

// ---------- Page -----------------------------------------------------------
export default function AssociationHome() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [tasksToReview, setTasksToReview] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [overdueStudents, setOverdueStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, a, t, ov] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/applications"),
          api.get("/tasks"),
          api.get("/association/overdue-students").catch(() => ({ data: { students: [] } })),
        ]);
        setStats(s.data.stats);
        setApps(a.data.applications || []);
        setAllTasks(t.data.tasks || []);
        setTasksToReview((t.data.tasks || []).filter((x) => x.status === "submitted"));
        setOverdueStudents(ov.data.students || []);
      } catch (e) {
        console.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Real-only tasks awaiting review — no demo fallback anymore
  const pendingHours = useMemo(() => tasksToReview.map((t) => ({
    id: t.id,
    student_name: t.student_name,
    program: t.title,
    hours: t.expected_hours,
    submitted_at: t.updated_at || t.created_at,
    note: t.student_submission?.slice(0, 60) || "",
  })), [tasksToReview]);

  const operationBuckets = useMemo(() => {
    const overdueIds = new Set(overdueStudents.map((x) => x.id || x.student_id));
    const rows = apps.filter((a) => ["accepted", "assigned"].includes(a.status));
    const counts = { "يحتاج تكليفًا": 0, "يعمل حاليًا": 0, "سلّم وينتظر التقييم": 0, "يحتاج متابعة": 0, "جاهز للإنهاء": 0 };
    rows.forEach((a) => {
      const tasks = allTasks.filter((t) => t.student_id === a.student_id);
      if (overdueIds.has(a.student_id)) counts["يحتاج متابعة"]++;
      else if (tasks.some((t) => t.status === "submitted")) counts["سلّم وينتظر التقييم"]++;
      else if (!tasks.length || !tasks.some((t) => ["open", "submitted"].includes(t.status))) counts["يحتاج تكليفًا"]++;
      else if (tasks.length && tasks.every((t) => t.status === "done")) counts["جاهز للإنهاء"]++;
      else counts["يعمل حاليًا"]++;
    });
    return counts;
  }, [apps, allTasks, overdueStudents]);

  const assigned = stats?.assigned_students ?? 0;
  const active = stats?.active_students ?? 0;

  return (
    <div data-testid="association-home">
      <AgreementAlert />
      {/* Header — clickable identity card to edit profile */}
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-widest text-teal-700">
          مساحة عمل الجمعية
        </div>
        <Link
          to="/dashboard/association-profile"
          className="group mt-2 flex items-start gap-4 rounded-2xl p-3 -m-3 hover:bg-white/70 transition-colors max-w-3xl"
          data-testid="assoc-header-card"
          title="اضغط لفتح الملف التعريفي وتعديله"
        >
          <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-teal-700 to-teal-900 grid place-items-center text-white overflow-hidden shrink-0 border border-teal-800/20">
            {user?.avatar_url ? (
              <SignedImage
                fileId={user.avatar_url}
                alt=""
                className="h-full w-full object-cover"
                fallback={<Building2 className="h-7 w-7" strokeWidth={1.5} />}
              />
            ) : (
              <Building2 className="h-7 w-7" strokeWidth={1.5} />
            )}
          </div>
          <div>
            <h1 className="text-2xl font-heading font-extrabold text-slate-900 group-hover:text-teal-800 transition-colors flex items-center gap-2">
              أهلاً {user?.organization_name || user?.name}
              <Edit3 className="h-4 w-4 text-teal-600 opacity-0 group-hover:opacity-100 transition-opacity" />
            </h1>
            <p className="mt-1 text-slate-600">
              إجراءات اليوم في مكان واحد: ساعات بانتظار المراجعة، طلاب متأخرون، مهام، وزيارات مقرّرة.
            </p>
            <div className="mt-1 text-[11px] text-teal-700 font-semibold inline-flex items-center gap-1">
              <Edit3 className="h-3 w-3" /> اضغط لتعديل معلومات الجمعية والشعار
            </div>
          </div>
        </Link>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard
          icon={Users}
          label="الطلاب الذين تم تسكينهم"
          value={assigned}
          tone="teal"
          to="/dashboard/students-directory"
        />
        <StatCard
          icon={GraduationCap}
          label="الطلاب النشطون"
          value={active}
          tone="emerald"
        />
        <StatCard
          icon={ClipboardCheck}
          label="بانتظار المراجعة"
          value={pendingHours.length}
          tone="amber"
        />
        <StatCard
          icon={AlertTriangle}
          label="طلاب متأخرون"
          value={overdueStudents.length}
          tone="rose"
        />
      </div>

      <section className="mb-6 rounded-2xl border border-cream-200 bg-white p-4 card-shadow" data-testid="association-operation-buckets">
        <h2 className="font-heading font-black text-slate-900">خريطة تشغيل الطلاب</h2>
        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">{Object.entries(operationBuckets).map(([label,value]) => <Link key={label} to="/dashboard/students-directory?filter=placed" className="rounded-xl border border-slate-100 bg-slate-50 p-3 hover:border-teal-300"><b className="block text-2xl text-teal-800">{value}</b><span className="text-xs text-slate-600">{label}</span></Link>)}</div>
      </section>

      {/* Journey Tracker removed */}

      {loading ? (
        <div className="grid gap-4 md:grid-cols-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-56 rounded-2xl bg-white border border-cream-200 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2">
          {/* Pending Hours */}
          <div
            className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow"
            data-testid="assoc-pending-hours"
          >
            <SectionHeader
              title="ساعات بانتظار المراجعة"
              count={pendingHours.length}
              hint="تسليمات الطلاب الأخيرة"
              action="فتح المهام"
              actionTo="/dashboard/tasks"
            />
            <div className="divide-y divide-cream-100">
              {pendingHours.slice(0, 4).map((row) => (
                <div key={row.id} className="py-3 flex items-start gap-3">
                  <div className="h-9 w-9 rounded-lg bg-amber-50 text-amber-700 grid place-items-center shrink-0">
                    <Clock className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-slate-900 text-sm">
                        {row.student_name}
                      </div>
                      {row.demo && <DemoBadge />}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate">
                      {row.program}
                      {row.hours ? (
                        <span className="arabic-numerals"> • {row.hours} ساعات</span>
                      ) : null}
                      {" • "}
                      <span>{timeAgoAr(row.submitted_at)}</span>
                    </div>
                    {row.note && (
                      <div className="text-xs text-slate-600 mt-1 line-clamp-1">
                        {row.note}
                      </div>
                    )}
                  </div>
                  <Link
                    to="/dashboard/tasks"
                    className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-teal-700 hover:text-teal-800"
                  >
                    مراجعة <ArrowLeft className="h-3 w-3" />
                  </Link>
                </div>
              ))}
              {pendingHours.length === 0 && (
                <EmptyRow icon={CheckCircle2} text="لا توجد ساعات بانتظار المراجعة" />
              )}
            </div>
          </div>

          {/* Overdue Students — real data */}
          <div
            className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow"
            data-testid="assoc-overdue-students"
          >
            <SectionHeader
              title="طلاب يحتاجون متابعة"
              count={overdueStudents.length}
              hint="حضور منخفض (<70%) أو مهام متأخرة"
              action="عرض الجميع"
              actionTo="/dashboard/tasks"
            />
            <div className="divide-y divide-cream-100">
              {overdueStudents.map((s) => (
                <div key={s.id} className="py-3 flex items-center gap-3" data-testid={`overdue-${s.id}`}>
                  <div className="h-9 w-9 rounded-lg bg-rose-50 text-rose-700 grid place-items-center shrink-0">
                    <AlertTriangle className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 text-sm">{s.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{s.major || "—"}</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {s.reasons.map((r, i) => (
                        <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-50 border border-rose-200 text-rose-700 font-semibold">{r}</span>
                      ))}
                    </div>
                  </div>
                  {s.attendance_pct != null && (
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-rose-700 arabic-numerals shrink-0">
                      <TrendingDown className="h-3.5 w-3.5" /> {s.attendance_pct}%
                    </span>
                  )}
                </div>
              ))}
              {overdueStudents.length === 0 && (
                <EmptyRow icon={CheckCircle2} text="كل الطلاب على المسار الصحيح" />
              )}
            </div>
          </div>

          {/* Upcoming Activities — real CRUD */}
          <div
            className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow"
            data-testid="assoc-upcoming-visits"
          >
            <AssociationActivities compact />
          </div>

          {/* Placement Timeline — full width */}
          <div className="md:col-span-2">
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyRow({ icon: Icon, text }) {
  return (
    <div className="py-8 flex flex-col items-center justify-center text-center gap-2 text-slate-500">
      <Icon className="h-8 w-8 text-slate-300" />
      <div className="text-sm">{text}</div>
    </div>
  );
}
